/**
 * ParentsSection -- 신랑/신부 정보 편집
 */

import { useState } from 'react'
import { Users, Eye } from 'lucide-react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { Checkbox, Input, Select, RadioGroup } from '../../ui/index.js'
import styles from './SectionForm.module.css'

const DISPLAY_MODE_OPTIONS = [
  { value: 'full_names', label: '부모님 성함 모두 표시' },
  { value: 'single_parent_father', label: '아버지만 표시' },
  { value: 'single_parent_mother', label: '어머니만 표시' },
  { value: 'self_only', label: '본인만 표시' },
]

const GROOM_RELATION_OPTIONS = [
  { value: '', label: '선택해주세요' },
  { value: '장남', label: '장남' },
  { value: '차남', label: '차남' },
  { value: '삼남', label: '삼남' },
  { value: '__custom__', label: '직접입력' },
]

const BRIDE_RELATION_OPTIONS = [
  { value: '', label: '선택해주세요' },
  { value: '장녀', label: '장녀' },
  { value: '차녀', label: '차녀' },
  { value: '삼녀', label: '삼녀' },
  { value: '__custom__', label: '직접입력' },
]

const PRESET_VALUES_GROOM = new Set(['', '장남', '차남', '삼남'])
const PRESET_VALUES_BRIDE = new Set(['', '장녀', '차녀', '삼녀'])

type PersonType = 'groom' | 'bride'

interface PersonCardProps {
  type: PersonType
  person: Record<string, unknown> | undefined
  updateNested: (path: string, value: unknown) => void
}

function RelationSelect({ type, value, onChange }: { type: PersonType; value: string; onChange: (v: string) => void }) {
  const options = type === 'groom' ? GROOM_RELATION_OPTIONS : BRIDE_RELATION_OPTIONS
  const presets = type === 'groom' ? PRESET_VALUES_GROOM : PRESET_VALUES_BRIDE
  const isCustom = value !== '' && !presets.has(value)
  const [showCustomInput, setShowCustomInput] = useState(isCustom)

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value
    if (v === '__custom__') {
      setShowCustomInput(true)
      onChange('')
    } else {
      setShowCustomInput(false)
      onChange(v)
    }
  }

  return (
    <div className={styles.fieldWithCheckbox}>
      <Select
        label="관계"
        value={showCustomInput ? '__custom__' : value}
        onChange={handleSelectChange}
        options={options}
      />
      {showCustomInput && (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="예: 독자, 사남"
        />
      )}
    </div>
  )
}

function PersonCard({ type, person, updateNested }: PersonCardProps) {
  const prefix = type
  const label = type === 'groom' ? '신랑' : '신부'
  const isDeceased = (person?.isDeceased as { father?: boolean; mother?: boolean }) ?? {}

  return (
    <div className={styles.personCard} data-person={type}>
      <div className={styles.personCardHeader}>
        <span className={styles.personCardIconBadge}>
          <Users size={20} />
        </span>
        <span className={styles.personCardLabel}>{label} 측</span>
      </div>
      <div className={styles.personCardDivider} />
      <div className={styles.personCardBody}>
        {/* 이름 + 관계 */}
        <div className={styles.row}>
          <Input
            label={`${label} 이름`}
            value={(person?.name as string) ?? ''}
            onChange={(e) => updateNested(`${prefix}.name`, e.target.value)}
            placeholder="성함"
          />
          <RelationSelect
            type={type}
            value={(person?.relation as string) ?? ''}
            onChange={(v) => updateNested(`${prefix}.relation`, v)}
          />
        </div>

        {/* 부모님 정보 */}
        <div className={styles.subSection}>
          <span className={styles.subSectionTitle}>부모님 정보</span>
          <div className={styles.row}>
            <div className={styles.fieldWithCheckbox}>
              <Input
                label="아버지 성함"
                value={(person?.fatherName as string) ?? ''}
                onChange={(e) => updateNested(`${prefix}.fatherName`, e.target.value)}
                placeholder="성함"
              />
              <Checkbox
                label="故人"
                checked={isDeceased.father ?? false}
                onChange={(v) => updateNested(`${prefix}.isDeceased.father`, v)}
              />
            </div>
            <div className={styles.fieldWithCheckbox}>
              <Input
                label="어머니 성함"
                value={(person?.motherName as string) ?? ''}
                onChange={(e) => updateNested(`${prefix}.motherName`, e.target.value)}
                placeholder="성함"
              />
              <Checkbox
                label="故人"
                checked={isDeceased.mother ?? false}
                onChange={(v) => updateNested(`${prefix}.isDeceased.mother`, v)}
              />
            </div>
          </div>
        </div>

        {/* 표시 방식 */}
        <div className={styles.subSection}>
          <span className={styles.subSectionLabel}>
            <Eye size={16} />
            표시 방식
          </span>
          <RadioGroup
            name={`${prefix}-displayMode`}
            value={(person?.displayMode as string) ?? 'full_names'}
            onChange={(v) => updateNested(`${prefix}.displayMode`, v)}
            options={DISPLAY_MODE_OPTIONS}
          />
        </div>
      </div>
    </div>
  )
}

export function ParentsSection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  if (!invitation) return null

  return (
    <div className={styles.personCardRow}>
      <PersonCard
        type="groom"
        person={invitation.groom as Record<string, unknown> | undefined}
        updateNested={updateNested}
      />
      <PersonCard
        type="bride"
        person={invitation.bride as Record<string, unknown> | undefined}
        updateNested={updateNested}
      />
    </div>
  )
}
